import { useCallback } from "react";
import { useRouteMatch } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useModal from "contexts/Modal/useModal";
import { PROJECTS, SCENES } from "common/routes.constants";
import { hasUnsavedSceneChanges } from "views/Projects/views/Scene/tabs/Scene/Editor/r3f/utils/sceneDirtyState";

/**
 * Guards anything in the top bar that navigates out of the open scene — the
 * organization logo's shortcut to the scene list, and all three switchers.
 * Leaving is what discards the edits: nothing about the editor's state survives
 * the route change, and none of these controls save on the way out.
 *
 * Returns a wrapper: call it with the navigation you were about to do. With
 * nothing to lose it runs straight away, so the ordinary case costs the user no
 * extra click.
 *
 * @example
 *   const confirmLeaveScene = useConfirmLeaveScene();
 *   onChange={(shortid) => confirmLeaveScene(() => history.push(...))}
 */
const useConfirmLeaveScene = () => {
	const { confirm } = useModal();
	const { t } = useTranslation();

	// Only an open scene can hold unsaved edits. The editor's Zustand stores are
	// module singletons that outlive the route, so without this the dialog could
	// also fire from, say, the library — asking about a scene closed long ago.
	const inSceneEditor = Boolean(
		useRouteMatch({
			path: `/${PROJECTS}/:projectId/${SCENES}/:sceneId`,
			exact: true,
		}),
	);

	return useCallback(
		(proceed: () => void) => {
			if (!inSceneEditor || !hasUnsavedSceneChanges()) {
				proceed();
				return;
			}
			confirm({
				title: String(t("app.leaveScene.modal.title")),
				content: String(t("app.leaveScene.modal.content")),
				okText: String(t("app.leaveScene.modal.discard")),
				// Not antd's `danger` prop — that fills the button red, which §1
				// does not do. See styles/AppGlobalStyle.tsx's .confirm-danger.
				okButtonProps: { className: "confirm-danger" },
				cancelText: String(t("app.leaveScene.modal.stay")),
				onOk: proceed,
			});
		},
		[inSceneEditor, confirm, t],
	);
};

export default useConfirmLeaveScene;
